import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Link, useStaticQuery, graphql } from 'gatsby';
import { darken } from 'polished';
import { useStoreState } from 'easy-peasy';
import ReactGA from 'react-ga';
import { filter } from 'lodash';

import config from '../utils/config';

const categoriesQuery = graphql`
  query allSanityCategory {
    allSanityCategory(sort: { fields: _key, order: ASC }) {
      edges {
        node {
          id
          slug {
            current
          }
          title
          parents {
            id
            slug {
              current
            }
          }
        }
      }
    }
  }
`;

const Container = styled.div`
  @media screen and (min-width: 1024px) {
    padding: 1.5rem 6.5rem 0rem 6.5rem !important;
  }
  padding-bottom: 0;
  nav {
    background: transparent !important;
  }
  .icon {
    color: #4ac959 !important;
  }

  .control {
    position: absolute;
    right: 0;
    top: 50px;
  }
  .navbar-item,
  .navbar-link {
    align-items: flex-start;
  }

  .navbar-link:focus,
  a.navbar-item:focus,
  a.navbar-item:focus-within,
  a.navbar-item:hover {
    background-color: transparent !important;
  }

  .cart-count {
    font-size: 0.8rem;
    color: ${(props) => props.theme.mainBrandColor};
    margin-top: -14px;
    margin-left: 2px;
    @media only screen and (max-width: 767px) {
      margin-top: -2.5rem;
      margin-left: 12px;
    }
  }
  .button {
    position: absolute;
  }
  .navbar-brand {
    font-size: 1.5rem;
    font-weight: 500;
  }

  .navbar-menu {
    background-color: transparent;
    @media only screen and (max-width: 1024px) {
      padding-left: 1rem;
      margin-bottom: 10px;
    }
  }
  .bottomNav {
    justify-content: center;
    margin-bottom: 1rem;
  }
  .navbar-item.is-hidden-mobile {
    display: flex !important;
    justify-content: center !important;
  }
  .navbar-burger {
    color: ${(props) => (props.textWhite ? '#ffff' : '#3D3D3D')} !important;
  }
  .navbar-start,
  .navbar-item.is-hidden-mobile,
  .navbar-end.is-hidden-mobile {
    width: 33%;
  }
  .navbar-item,
  .navbar-link {
    color: ${(props) => (props.textWhite ? '#ffff' : '#3D3D3D')};
    :hover {
      transition: 0.6s;
      color: ${(props) => props.theme.mainBrandColor} !important;
    }
  }

  .navbar-link:not(.is-arrowless) {
    padding-right: 0.5;
  }
  .navbar-link:not(.is-arrowless)::after {
    display: none;
  }
  .has-text-centered {
    justify-content: center;
  }
  .navbar-item.has-dropdown:hover .navbar-link,
  a.navbar-item:hover {
    background-color: transparent;
    color: ${(props) => darken(0.75, props.theme.lightShades)};
  }
  @media screen and (min-width: 1024px) {
    .navbar-menu {
      margin-right: -0.75rem;
      margin-top: -12px !important;
    }
  }
  @media only screen and (min-width: 1024px) {
    .navbar-dropdown {
      background: #282828;
      a.navbar-item {
        color: #a0a0a0;
      }
      a.navbar-item:hover {
        color: #ffffff;
      }
    }
  }
  .top-banner {
    margin-top: 10px;
  }
`;

const LogoImg = styled.img`
  height: 60px;
  max-height: 3.7rem !important;
  width: auto;
  @media only screen and (max-width: 767px) {
    max-height: 3rem !important;
  }
`;

const MobileCart = styled.div`
  margin-left: auto;
  margin-top: 5px;
`;

const Header = ({ textWhite, value }) => {
  const [isActive, setIsActive] = useState(false);
  const cartItems = useStoreState((state) => state.cart.items);
  const data = useStaticQuery(categoriesQuery);
  const [searchQuery, setSearchQuery] = useState('');

  // filter categories
  const allCategories = data.allSanityCategory.edges;
  const parentCategories = filter(
    allCategories,
    (o) => o.node.parents === null,
  );
  const categories = parentCategories.map((o) => {
    const children = filter(allCategories, (oc) => {
      return oc.node.parents !== null && oc.node.parents.id === o.node.id;
    });
    return { ...o.node, children };
  });
  return (
    <Container className="section" textWhite={textWhite}>
      <nav className="navbar" role="navigation" aria-label="main navigation">
        <div className="container">
          <div className="navbar-brand">
            <Link
              to="/"
              className="navbar-item is-hidden-tablet is-hidden-desktop"
            >
              <LogoImg src="/images/logo.png" alt={config.siteName} />
            </Link>
            <MobileCart className="is-hidden-tablet is-hidden-mobile">
              <Link to="/cart" className="navbar-item">
                <i className="fas fa-shopping-cart" />
                <div className="cart-count">{cartItems.length}</div>
              </Link>
              <Link to="/cart" className="navbar-item">
                <i className="fas fa-shopping-cart" />
              </Link>
            </MobileCart>
            <a
              role="button"
              className={`navbar-burger burger ${isActive ? 'is-active' : ''}`}
              aria-label="menu"
              aria-expanded="false"
              data-target="navbarMain"
              onClick={() => {
                setIsActive(!isActive);
                ReactGA.event({
                  category: 'User',
                  action: 'Toggle Mobile Menu',
                });
              }}
            >
              <span aria-hidden="true" />
              <span aria-hidden="true" />
              <span aria-hidden="true" />
            </a>
          </div>
          <p className="is-hidden-desktop is-hidden-tablet is-flex has-text-centered">
            <Link
              to="/cart"
              className="navbar-item is-size-5 has-text-black-ter"
            >
              <i className="fas fa-shopping-cart" />
              <div className="cart-count">{cartItems.length}</div>
            </Link>
          </p>
          <div
            id="navbarMain"
            className={`navbar-menu ${isActive ? 'is-active' : ''}`}
          >
            <div className="navbar-start is-hidden-mobile top-banner">
              Free shipping in India
            </div>
            <div className="is-hidden-mobile">
              <Link to="/" className="navbar-item">
                <LogoImg src="/images/logo.png" alt={config.siteName} />
              </Link>
            </div>
            <div className="navbar-end is-hidden-mobile top-banner ">
              <Link to="/cart" className="navbar-item is-size-5">
                <i className="fas fa-shopping-cart" />
                <div className="cart-count">{cartItems.length}</div>
              </Link>
            </div>
            <div className="field has-addons">
              <div className="control">
                <input
                  name="Search"
                  className="input"
                  placeholder="Search"
                  type="text"
                  onChange={(e) => setSearchQuery(e.target.value)}
                  value={value}
                />
              </div>
              <div className="control">
                <Link
                  to={`/shop?query=${searchQuery}`}
                  className="button is-warning has-text-white"
                  type="button"
                >
                  <span>Search</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </nav>
      <div
        id="navbarMain"
        className={`bottomNav navbar-menu ${isActive ? 'is-active' : ''}`}
      >
        <div className="has-text-centered is-flex-desktop">
          {categories &&
            categories.map((category) => (
              <div
                key={category.id}
                className="navbar-item has-dropdown is-hoverable is-size-5 has-text-whiteproduct-info-btn"
              >
                <Link to={`/${category.slug.current}`} className="navbar-link">
                  {category.title}
                </Link>
                {category.children && category.children.length > 0 ? (
                  <div className="navbar-dropdown">
                    {category.children.map(({ node: childCategory }) => (
                      <Link
                        key={childCategory.id}
                        to={`/${childCategory.slug.current}`}
                        className="navbar-item is-size-5"
                      >
                        {childCategory.title}
                      </Link>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          <Link to="/shop" className="navbar-link is-size-5">
            Shop
          </Link>
          <Link to="/contact" className="navbar-link is-size-5">
            Contact
          </Link>
        </div>
      </div>
    </Container>
  );
};

Header.defaultProps = {
  home: {},
};

Header.propTypes = {
  home: PropTypes.object,
};

export default Header;
